import { useQuery } from "@tanstack/react-query";
import { useUser } from "@clerk/clerk-react";
import { clientService } from "@/lib/api/services/client.service";

/**
 * Hook to get the client profile ID for a given user ID
 * This is needed to get the UUID primary key of the client_profiles table
 */
export function useClientProfileId(userId: string | undefined) {
  const { user } = useUser();
  const isCurrentUser = userId === user?.id;

  const { data: profileIdData, isLoading } = useQuery({
    queryKey: ["client-profile-id", userId],
    queryFn: async () => {
      if (!userId) return null;

      // If it's the current user, use the API endpoint
      if (isCurrentUser) {
        try {
          const result = await clientService.getMyProfileId();
          return result.id;
        } catch (err: any) {
          // If profile doesn't exist, return null
          if (err.code === 'NOT_FOUND' || err.status === 404) {
            return null;
          }
          throw err;
        }
      }

      // For other users, we'd need a different endpoint or keep using Supabase
      // For now, return null and let the caller handle it
      // TODO: Add GET /api/v1/profiles/client/user/:userId endpoint if needed
      return null;
    },
    enabled: !!userId && isCurrentUser,
    staleTime: 5 * 60 * 1000, // 5 minutes
    refetchOnMount: false, // Prevent duplicate calls in React Strict Mode
    refetchOnWindowFocus: false, // Profile ID rarely changes
  });

  return { profileId: profileIdData ?? null, isLoading };
}